import type { CartesianScaleOptions } from "chart.js";

import { ColumnTotalType } from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import {
  IReportChartDisplayModeOption,
  ReportChartDisplayMode,
} from "./report-chart.models";

export const METRIC_TOTAL_TYPES: ColumnTotalType[] = [
  "SUM",
  "AVG",
  "MIN",
  "MAX",
];

/** Y-axis value: aggregate by all report grouping columns (table row identity). */
export const REPORT_CHART_GROUPING_DIMENSION_VALUE = "__report_grouping__";

/** Internal composite key part separator (not shown in UI). */
export const REPORT_CHART_GROUPING_PART_SEPARATOR = "\u241F";

/** RTL-neutral separator between grouping criteria on chart labels. */
export const REPORT_CHART_GROUPING_DISPLAY_SEPARATOR = " • ";

export const REPORT_CHART_DISPLAY_MODE_OPTIONS: IReportChartDisplayModeOption[] =
  [
    {
      value: ReportChartDisplayMode.solid,
      labelKey: "reports.chart.displayMode.solid",
    },
    {
      value: ReportChartDisplayMode.colorByValue,
      labelKey: "reports.chart.displayMode.colorByValue",
    },
    {
      value: ReportChartDisplayMode.differentColors,
      labelKey: "reports.chart.displayMode.differentColors",
    },
    {
      value: ReportChartDisplayMode.lineChart,
      labelKey: "reports.chart.displayMode.lineChart",
    },
    {
      value: ReportChartDisplayMode.pieChart,
      labelKey: "reports.chart.displayMode.pieChart",
    },
  ];

export const REPORT_CHART_DISPLAY_MODE_FIELD_WIDTH = 168;

export const REPORT_CHART_DEFAULT_DISPLAY_MODE = ReportChartDisplayMode.solid;

/** Distinct bar colors for "different colors" mode. */
export const REPORT_CHART_BAR_PALETTE: readonly string[] = [
  "#30cd89",
  "#5c6bc0",
  "#ff7043",
  "#26a69a",
  "#ab47bc",
  "#ffa726",
  "#42a5f5",
  "#ec407a",
  "#7e57c2",
  "#66bb6a",
  "#ef5350",
  "#29b6f6",
];

export const REPORT_CHART_VALUE_COLOR_ALPHA = 0.9;
export const REPORT_CHART_BAR_PALETTE_REFRESH_OFFSET = 7;

export const REPORT_CHART_LINE_POINT_RADIUS = 5;
export const REPORT_CHART_LINE_BORDER_WIDTH = 2;

/* eslint-disable @typescript-eslint/no-magic-numbers */
export const REPORT_CHART_VALUE_GRADATION_COUNT = 8;
export const REPORT_CHART_VALUE_GRADATION_COLORS: readonly [
  r: number,
  g: number,
  b: number,
][] = [
  [220, 53, 69],
  [253, 126, 20],
  [255, 193, 7],
  [144, 176, 56],
  [64, 140, 64],
  [32, 118, 54],
  [12, 95, 48],
  [0, 72, 36],
];
export const REPORT_CHART_VALUE_GRADATION_EXPONENT = 3;

/** Below this share of max — label does not fit inside the bar. */
export const REPORT_CHART_LABEL_INSIDE_MIN_RATIO = 0.12;
export const REPORT_CHART_LABEL_OUTSIDE_OFFSET = 6;
export const REPORT_CHART_LINE_DATALABEL_OFFSET = 16;
export const REPORT_CHART_LINE_DATALABEL_PADDING = 4;
export const REPORT_CHART_LINE_DATALABEL_BORDER_RADIUS = 4;
export const REPORT_CHART_GRID_COLOR = "#D0D7E2";
export const REPORT_CHART_LABEL_MAX_LENGTH = 28;
export const REPORT_CHART_TRUNCATE_LABEL_ELLIPSIS_LENGTH = 3;

/** 0 = no slice; all rows are shown (scroll when compact). */
export const REPORT_CHART_MAX_ENTITIES_DISPLAY = 0;
/** Above this count: thin bars, outside labels, vertical scroll. */
export const REPORT_CHART_COMPACT_MODE_THRESHOLD = 15;
export const REPORT_CHART_BAR_THICKNESS_DEFAULT = 22;
export const REPORT_CHART_BAR_THICKNESS_COMPACT = 8;
export const REPORT_CHART_SCROLL_ROW_HEIGHT = 28;
export const REPORT_CHART_SCROLL_CHART_PADDING = 40;
export const REPORT_CHART_LAYOUT_PADDING_RIGHT_DEFAULT = 28;
export const REPORT_CHART_LAYOUT_PADDING_RIGHT_COMPACT = 44;
export const REPORT_CHART_LINE_EXTRA_LAYOUT_PADDING = 16;
export const REPORT_CHART_PIE_MIN_LABEL_PERCENT = 4;
export const REPORT_CHART_TICK_FONT_SIZE_DEFAULT = 12;
export const REPORT_CHART_TICK_FONT_SIZE_COMPACT = 11;
export const REPORT_CHART_DATALABEL_FONT_SIZE_DEFAULT = 12;
export const REPORT_CHART_DATALABEL_FONT_SIZE_COMPACT = 11;

export type ReportChartAxisTitleStyle = Pick<
  CartesianScaleOptions["title"],
  "color" | "font"
>;

export const REPORT_CHART_AXIS_TITLE_STYLE: ReportChartAxisTitleStyle = {
  color: rydeColors.reportChartAxisTitle,
  font: { size: 14, weight: 600 },
};
